// Climate mode's entry in the viewer: a "Climate" button under the distance
// readout whenever the Earth is the focused body, which loads the mode
// (mode.js: the data shell and its card) the first time it is pressed, and
// #climate[/<layer>[/<year, month or map>]] links, which focus the Earth
// and open it there (/viewer/#climate/ice/2012, #climate/life/7,
// #climate/energy/reflect). The /climate/ page links in this way.

const CSS = `
.cm-chip { position: fixed; top: 68px; left: 50%; transform: translateX(-50%); z-index: 50; display: inline-flex; align-items: center; gap: 8px;
  min-height: 36px; padding: 0 14px 0 12px; background: var(--sb-bg, #0a0d13); border: none; border-radius: 10px;
  color: var(--sb-ink, #ece7da); font: 500 13px 'Inter', sans-serif; cursor: pointer; transition: background-color .15s ease; }
.cm-chip:hover { background: var(--sb-fill-hover, #1b212c); }
.cm-chip:focus-visible { outline: 2px solid var(--sb-accent, #7dd3a8); outline-offset: 2px; }
.cm-chip[hidden] { display: none; }
.cm-chip svg { width: 14px; height: 14px; stroke: var(--sb-accent, #7dd3a8); fill: none; stroke-width: 1.4; }
.cm-chip span { color: var(--sb-ink-2, #a8a396); font-weight: 400; }
body.climate-mode .cm-chip,
body.fly-mode .cm-chip, body.scale-mode .cm-chip, body.documentary-mode .cm-chip, body.mode-screensaver .cm-chip,
body.rocketry-mode .cm-chip, body.eclipse-mode .cm-chip, body.photo-mode-clean .cm-chip, body.warp-active .cm-chip,
body.game-mode .cm-chip, body.lesson-mode .cm-chip, body.cupola-mode .cm-chip, body.astro-mode .cm-chip { display: none !important; }
@media (max-width: 820px), (pointer: coarse) and (max-height: 600px) { .cm-chip { top: 76px; } }
`;
const GLOBE = '<svg viewBox="0 0 14 14" aria-hidden="true"><circle cx="7" cy="7" r="5.6"/><path d="M1.6 5.2c2.2 1 3.4.6 4.6-.4s2.4-1.2 3.6 0 2 .9 2.6.3M2.2 9.6c1.6-.8 3-.4 4.2.5s2.6.9 4.4-.3"/></svg>';

// The address the page opened with, read before the startup's focus moves
// rewrite it.
const OPENED_ON = typeof window !== 'undefined' && /^#\/?climate\b/i.test(window.location.hash) ? window.location.hash : null;

// api: everything mode.js takes, plus focusEarth() and blockedBy().
export function initClimateMode(api) {
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);
  const chip = document.createElement('button');
  chip.type = 'button';
  chip.className = 'cm-chip';
  chip.hidden = true;
  chip.innerHTML = `${GLOBE}Climate<span>warming, ice, life</span>`;
  chip.title = 'NASA and NOAA climate data on this Earth';
  document.body.appendChild(chip);

  let mode = null;
  let loading = null;
  function load() {
    if (!loading) {
      loading = import('./mode.js')
        .then((m) => { mode = m.createClimateMode({ ...api, onClose: () => sync() }); return mode; })
        .catch((err) => { console.warn('[climate] unavailable', err); loading = null; throw err; });
    }
    return loading;
  }

  function sync() {
    const blocked = api.blockedBy();
    // Fly mode, rocketry and the other modes hide the card; the data on the
    // globe goes with it.
    if (blocked && mode && mode.open) mode.close();
    chip.hidden = !(api.isEarthFocused() && !blocked && !(mode && mode.open));
  }
  setInterval(sync, 400);

  async function openAt(layer, value) {
    const m = await load();
    await m.show(layer, value);
    sync();
  }
  chip.addEventListener('click', () => openAt().catch(() => {}));

  // A #climate link: focus the Earth, wait for the camera to land, open.
  function fromHash(hash) {
    const [, layer, value] = /^#\/?climate(?:\/([a-z-]+))?(?:\/([a-z0-9-]+))?/i.exec(hash || '') || [];
    const name = { warming: 'temp', temperature: 'temp', temp: 'temp', ice: 'ice', 'sea-ice': 'ice', life: 'life', energy: 'energy', ozone: 'ozone' }[(layer || '').toLowerCase()] || 'temp';
    if (!api.isEarthFocused()) api.focusEarth();
    load().catch(() => {});
    const started = Date.now();
    let settled = 0;
    const wait = () => {
      settled = api.isEarthFocused() && !api.isAnimating() ? settled + 1 : 0;
      if (settled >= 3) { openAt(name, value).catch(() => {}); return; }
      if (Date.now() - started < 30000) setTimeout(wait, 300);
    };
    setTimeout(wait, 600);
    return true;
  }

  if (OPENED_ON) setTimeout(() => fromHash(OPENED_ON), 1200);

  return {
    frame() { if (mode) mode.frame(); },
    fromHash,
    close() { mode?.close(); },
    get open() { return Boolean(mode && mode.open); },
    // A layer is on the globe (the cloud volumes stand aside, main.js).
    get coversGround() { return Boolean(mode && mode.coversGround); },
  };
}
